import { FC, memo, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import LoadingData from 'components/LoadingData';
import axios from '../../utils/axios';

import {
  CategoryTpe,
  CategoryName,
  CategoriesType,
  LegacyDataType,
  LegacyResponseType,
} from '../../@types/legacy';

type LegacyCategoriesType = {
  activeCategory: string;
  handleCategory: (value: string) => void;
};

type LegacyCategoryType = {
  item: CategoryTpe;
  activeCategory: string;
};

type LegacyViewType = {
  activeCategory: string;
  data: LegacyDataType[];
};

type LegacyStateType = {
  page: number;
  isLoading: boolean;
  hasNextPage: boolean;
  data: LegacyDataType[];
};

export const categories: CategoriesType = [
  { name: CategoryName.DIVIDENS, label: 'Modificări legislative (Nou)' },
  { name: CategoryName.TVA, label: 'Resurse legislative' },
  { name: CategoryName.CHECKOUT, label: 'Ghiduri practice' },
];

const LegacyCategory: FC<LegacyCategoryType> = memo(
  ({ item, activeCategory }) => (
    <NavLink
      to={`/legacy/${item.name}`}
      className={cn('legacy-cat__item', {
        'legacy-cat__item--active': activeCategory === item.name,
      })}
    >
      <div className="legacy-cat__item-dot" />
      <span>{item.label}</span>
    </NavLink>
  ),
  (prev, next) => {
    if (prev.item.name !== next.item.name) return false;
    if ((prev.activeCategory === prev.item.name) !== (next.activeCategory === next.item.name)) return false;
    return true;
  },
);

const LegacyCategories: FC<LegacyCategoriesType> = memo(
  ({ activeCategory, handleCategory = () => {} }) => {
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      if (params.category) {
        const category = categories.find((i) => i.name === params.category);
        if (category) {
          handleCategory(category.name);
        } else {
          navigate(`/legacy/${categories[0].name}`);
          handleCategory(categories[0].name);
        }
      } else {
        navigate(`/legacy/${categories[0].name}`);
      }
    }, [navigate, params, handleCategory]);

    return (
      <div className="legacy-cat">
        <div className="legacy-cat__header">
          <span className="legacy-cat__label">Categorii</span>
        </div>
        <div className="legacy-cat__list">
          {categories.map((item) => (
            <LegacyCategory
              key={item.name}
              item={item}
              activeCategory={activeCategory}
            />
          ))}
        </div>
      </div>
    );
  },
  (prev, next) => prev.activeCategory === next.activeCategory,
);

const LegacyView: FC<LegacyViewType> = ({ activeCategory, data }) => {
  if (data.length === 0) {
    return (
      <div className="legacy-empty">
        <div className="legacy-empty__icon">📄</div>
        <p>Nu există articole în această categorie momentan.</p>
      </div>
    );
  }

  return (
    <div className="legacy-list">
      {data.map((e, i) => (
        <NavLink
          key={`${activeCategory}-legacy-item-${e.id}-${i}`}
          to={`/legacy/info/${e.id}`}
          className="legacy-card"
        >
          <div className="legacy-card__content">
            <h3 className="legacy-card__title">{e.title}</h3>
            <p className="legacy-card__excerpt">{e.excerpt}</p>
          </div>
          <div className="legacy-card__arrow">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
        </NavLink>
      ))}
    </div>
  );
};

const Legacy = () => {
  const [activeCategory, setActiveCategory] = useState('');
  const [{ data, page, hasNextPage, isLoading }, setState] = useState<LegacyStateType>({
    page: 1,
    data: [],
    isLoading: false,
    hasNextPage: false,
  });

  const makeRequest = useCallback(
    async (page = 1) => {
      if (activeCategory) {
        setState((old) => ({ ...old, isLoading: true }));
        try {
          const { data, links } = await axios
            .get<LegacyResponseType>('/api/v1/articles', {
              params: { page, category: activeCategory },
            })
            .then((res) => res.data);
          setState({ isLoading: false, data, page, hasNextPage: !!links.next });
        } catch (error) {
          setState((old) => ({ ...old, isLoading: false }));
        }
      }
    },
    [activeCategory],
  );

  useEffect(() => { makeRequest(); }, [makeRequest]);

  const handleCategory = useCallback((value: string) => setActiveCategory(value), []);
  const handleNextPage = useCallback((page: number) => makeRequest(page), [makeRequest]);

  const activeCategoryLabel = categories.find(c => c.name === activeCategory)?.label || '';

  return (
    <div className="legacy-page">
      <section className="legacy-hero">
        <div className="container">
          <span className="legacy-hero__label">Taxely Finance</span>
          <h1>Informații <span>Legislative</span></h1>
          <p>Rămâi la curent cu cele mai recente modificări fiscale și legislative care îți afectează afacerea.</p>
        </div>
      </section>

      <section className="legacy-content">
        <div className="container">
          <div className="legacy-layout">
            <div className="legacy-sidebar">
              <LegacyCategories
                activeCategory={activeCategory}
                handleCategory={handleCategory}
              />
            </div>

            <div className="legacy-main">
              {activeCategoryLabel && (
                <div className="legacy-main__header">
                  <h2>{activeCategoryLabel}</h2>
                  <div className="legacy-main__header-line" />
                </div>
              )}

              {isLoading ? (
                <LoadingData />
              ) : (
                <LegacyView activeCategory={activeCategory} data={data} />
              )}

              {(page > 1 || hasNextPage) && (
                <div className="legacy-pagination">
                  <button
                    type="button"
                    disabled={isLoading || page === 1}
                    onClick={() => handleNextPage(page - 1)}
                    className="legacy-pagination__btn"
                  >
                    ← Anterior
                  </button>
                  <span className="legacy-pagination__page">Pagina {page}</span>
                  <button
                    type="button"
                    disabled={isLoading || !hasNextPage}
                    onClick={() => handleNextPage(page + 1)}
                    className="legacy-pagination__btn"
                  >
                    Următor →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Legacy;